import {
    DestroyRef,
    Inject,
    Injectable,
    Injector,
    Signal,
    WritableSignal,
    computed,
    effect,
    signal,
} from '@angular/core';
import { TableColumnDef } from '../../../../../../projects/grid-views/src/public-api';
import { MatTableDataSource } from '@angular/material/table';
import { Translation, TranslocoService } from '@ngneat/transloco';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TestScenario } from 'app/api/model/testScenario';
import { Process, TestScenarioService } from 'app/api';
import { ProcessService } from 'app/modules/bp-management/process.service';
import { TypeOfProcess } from '@em4cloud/process-tree';
import { Observable } from 'rxjs';
import { TestService } from '../../test.service';
import { AlertService } from 'app/core/alert/alert.service';
import { SaveButtonService } from 'app/core/save-button/save-button.service';
import { LanguageChangerService } from 'app/services/translate_api/languageChanger.service';

export interface TestScenariosTableConfig {
    displayedColumns: Array<TableColumnDef>;
    dataSource: MatTableDataSource<TestScenario>;
}

@Injectable({
    providedIn: 'root',
})
export class TestScenariosService {
    // test scenarios (for a single process)
    private _testScenarios: WritableSignal<Array<TestScenario>> = signal([]);
    private _tableConfig: Signal<TestScenariosTableConfig>;
    private _translocoContent: Signal<Translation>;
    componentName = 'TestScenarioService';

    private _selectedProcess: Signal<Process>;
    private _selectedScenario = signal<TestScenario>(null);

    private _editedScenarios: WritableSignal<Map<string, TestScenario>> = signal(new Map());

    constructor(
        private _translocoService: TranslocoService,
        private _testScenarioAPIService: TestScenarioService,
        private _processService: ProcessService,
        private _destroyRef: DestroyRef,
        private _testService: TestService,
        private _alertService: AlertService,
        private _saveButtonService: SaveButtonService,
        private languageChangerService: LanguageChangerService,
        private injector: Injector
    ) {
        this._translocoContent = toSignal(this._translocoService.selectTranslation('test'), {
            initialValue: {},
        });
        this.languageChangerService.register(
            this.componentName,
            this.buildTestScenarios.bind(this)
        );
        this._selectedProcess = computed(this._processService.selectedProcess);
        this.buildTestScenarios();
        this.buildSelectedScenario();
        this.setTableConfig();
    }
    get testScenarios() {
        return this._testScenarios();
    }

    get tableConfig() {
        return this._tableConfig();
    }

    get selectedScenario() {
        return this._selectedScenario();
    }

    set selectedScenario(value: TestScenario) {
        this._selectedScenario.set(value);
    }

    get editedScenarios() {
        return this._editedScenarios.asReadonly();
    }

    buildTestScenarios() {
        // getting test scenarios for the selected process
        effect(
            () => {
                if (this._processService.view !== TypeOfProcess.Scenario) return;
                if (!this._selectedProcess()) {
                    this._testScenarios.set([]);
                    this._editedScenarios.set(new Map());
                    return;
                }
                this._testScenarioAPIService
                    .backendApiTestScenarioGet(this._selectedProcess().id)
                    .pipe(takeUntilDestroyed(this._destroyRef))
                    .subscribe(data => {
                        if (data && data.length > 0) {
                            this._testScenarios.set(data);
                        } else {
                            this._testScenarios.set([]);
                            this._editedScenarios.set(new Map());
                        }
                    });
            },
            { injector: this.injector }
        );

        // updating original test scenarios variable whenever a test scenario is edited
        effect(
            () => {
                let editedScenarios = this._editedScenarios();
                this._testScenarios.update(scenarios => {
                    scenarios.forEach((scenario, index) => {
                        if (editedScenarios.has(scenario.id)) {
                            scenario = {
                                ...scenario,
                                ...editedScenarios.get(scenario.id),
                            };
                            scenarios[index] = scenario;
                        }
                    });
                    return [...scenarios];
                });
            },
            { injector: this.injector }
        );
    }

    buildSelectedScenario() {
        // resetting the selected scenario whenever selected process changes
        effect(
            () => {
                if (this._selectedProcess()) this.selectedScenario = null;
                else this.selectedScenario = null;
            },
            { injector: this.injector }
        );
    }

    setTableConfig() {
        this._tableConfig = computed(() => {
            return {
                displayedColumns: [
                    {
                        columnKey: 'description',
                        columnName: this._translocoContent()['description'],
                    },
                ],
                dataSource: new MatTableDataSource(this._testScenarios()),
            };
        });
    }

    createNewScenario(scenario: any): Observable<any> {
        scenario = { ...scenario, bp_id: this._selectedProcess().id };
        return new Observable(observer => {
            this._testScenarioAPIService
                .backendApiTestScenarioPost(scenario)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(
                    data => {
                        scenario = data;
                        this._testScenarios.update(scenarios => [...scenarios, scenario]);
                        this._testService.updateTestScenarios(scenario);
                        this._alertService.successAlert('test', 'scenario_created', 'created');
                        observer.next(data);
                    },
                    error => {
                        observer.error(error);
                    }
                );
        });
    }

    deleteScenario() {
        const scenarioId = this._selectedScenario().id;
        if (scenarioId) {
            this._testScenarioAPIService
                .backendApiTestScenarioIdDelete(scenarioId)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(
                    data => {
                        this._testScenarios.update(scenarios => {
                            return [...scenarios.filter(scenario => scenario.id !== scenarioId)];
                        });
                        this._testService.testScenarios = this._testScenarios();
                        this._editedScenarios.update(scenarios => {
                            scenarios.delete(scenarioId);
                            return new Map(scenarios);
                        });
                        this.selectedScenario = null;
                        this._alertService.successAlert('test', 'scenario_deleted', 'deleted');
                    },
                    error => {}
                );
        }
    }

    updateEditedScenarios(scenario: TestScenario) {
        scenario = { ...scenario, id: this._selectedScenario().id };
        this._editedScenarios.update(scenarios => {
            scenarios.set(scenario.id, scenario);
            let newScenarios = new Map(scenarios);
            return newScenarios;
        });
    }

    save() {
        let scenarios = [];
        this._editedScenarios().forEach(edit => {
            let editedScenario = this._testScenarios().find(scenario => scenario.id === edit.id);
            if (editedScenario) {
                scenarios.push({ ...editedScenario, ...edit });
            }
        });
        this._testScenarioAPIService
            .backendApiTestScenarioPut(scenarios)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(data => {
                this._editedScenarios.set(new Map());
                this._alertService.successAlert('test', 'scenario_saved', 'saved');
                this._saveButtonService.hasUnsavedChanges = false;
            });
    }
}
